<template>
  <div class="share-modal-overlay" v-if="visible" @click="handleOverlayClick">
    <div class="share-modal" @click.stop>
      <div class="share-header">
        <h3>分享到</h3>
        <button class="close-btn" @click="handleClose">
          <span>&times;</span>
        </button>
      </div>
      
      <div class="share-content">
        <div class="share-preview">
          <div class="preview-title">{{ title }}</div>
          <div class="preview-url">{{ url }}</div>
        </div>
        
        <div class="share-grid">
          <div 
            v-for="(config, key) in platforms" 
            :key="key"
            class="share-item"
            :class="{ 'mobile-only': config.mobileOnly }"
            @click="handleShare(key)"
          >
            <div class="share-icon">
              <img :src="config.icon" :alt="config.name" />
            </div>
            <span class="share-name">{{ config.name }}</span>
          </div>
        </div>
      </div>
      
      <div class="share-footer">
        <span>提示：微信、小红书请在手机端打开分享</span>
      </div>
    </div>
  </div>
</template>

<script>
import { shareUrls, generateShareUrl, copyToClipboard } from '@/utils/share'

export default {
  name: 'ShareModal',
  props: {
    visible: Boolean,
    title: String,
    url: String,
    description: String
  },
  computed: {
    platforms() {
      return shareUrls
    }
  },
  methods: {
    handleShare(platform) {
      const config = shareUrls[platform]
      if (!config) return
      
      if (platform === 'copy') {
        this.handleCopy()
        return
      }
      
      if (config.copyLink) {
        this.handleCopy()
        return
      }
      
      const shareUrl = generateShareUrl(platform, {
        url: this.url || window.location.href,
        title: this.title || document.title,
        desc: this.description || ''
      })
      
      if (shareUrl) {
        window.open(shareUrl, '_blank', 'width=600,height=400')
      }
    },
    
    async handleCopy() {
      const text = this.url || window.location.href
      const success = await copyToClipboard(text)
      if (success) {
        this.$message.success('链接已复制到剪贴板')
        this.handleClose()
      } else {
        this.$message.error('复制失败')
      }
    },
    
    handleClose() {
      this.$emit('close')
    },
    
    handleOverlayClick() {
      this.handleClose()
    }
  }
}
</script>

<style scoped>
.share-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.share-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.share-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.share-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  line-height: 1;
}

.close-btn:hover {
  color: #666;
}

.share-content {
  padding: 20px;
}

.share-preview {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.preview-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.preview-url {
  font-size: 12px;
  color: #999;
  word-break: break-all;
}

.share-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.share-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 12px 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.share-item:hover {
  background: #f5f5f5;
  transform: translateY(-2px);
}

.share-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  overflow: hidden;
}

.share-icon img {
  width: 22px;
  height: 22px;
}

.share-name {
  font-size: 12px;
  color: #666;
  text-align: center;
}

.mobile-only {
  opacity: 0.7;
}

.mobile-only::after {
  content: '手机';
  font-size: 10px;
  color: #999;
}

.share-footer {
  padding: 12px 20px;
  background: #fafafa;
  text-align: center;
}

.share-footer span {
  font-size: 12px;
  color: #999;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .share-modal {
    width: 95%;
    max-width: 360px;
    margin: 10px;
  }
  
  .share-header {
    padding: 12px 16px;
  }
  
  .share-header h3 {
    font-size: 16px;
  }
  
  .share-content {
    padding: 16px;
  }
  
  .share-preview {
    padding: 10px 12px;
    margin-bottom: 16px;
  }
  
  .preview-title {
    font-size: 13px;
  }
  
  .preview-url {
    font-size: 11px;
  }
  
  .share-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  
  .share-item {
    padding: 10px 6px;
  }
  
  .share-icon {
    width: 36px;
    height: 36px;
  }
  
  .share-icon img {
    width: 20px;
    height: 20px;
  }
  
  .share-name {
    font-size: 11px;
  }
  
  .share-footer {
    padding: 10px 16px;
  }
  
  .share-footer span {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .share-modal {
    width: 92%;
    max-width: 320px;
  }
  
  .share-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  
  .share-item {
    padding: 8px 4px;
  }
  
  .share-icon {
    width: 32px;
    height: 32px;
  }
  
  .share-icon img {
    width: 18px;
    height: 18px;
  }
  
  .share-name {
    font-size: 10px;
  }
}
</style>
